import { axiosRubyClient } from '@/util/axios/axiosClient';

import {
  BillingAccountV2,
  CreateLocationPayload,
  CreateOrderPayloadV2,
  CreateOrderResponseV2,
  CreateSavedSnippetPayload,
  CreateShiftSupervisorPayload,
  LocationListItem,
  OrderTemplateDetail,
  OrderTemplateListItem,
  PaymentMethodsResponse,
  RateQuoteV2,
  RateRangeV2,
  SavedSnippet,
  SavedSnippetFieldKind,
  ShiftSupervisor,
  SkillListItem,
  UniformListItem,
  WorkerCounts,
  WorkerListItem,
  WorkerSearchParams,
} from './types';

type ApiResponse<T> = {
  success: boolean;
  data: T;
  errors?: string[];
};

export const createOrderV2Client = {
  async listBillingAccounts() {
    const { data } = await axiosRubyClient.get<ApiResponse<BillingAccountV2[]>>(
      '/api/admin/v2/billing_accounts',
    );
    return data.data;
  },

  async listOrderTemplates(accountId: number, orderType?: string) {
    const { data } = await axiosRubyClient.get<
      ApiResponse<OrderTemplateListItem[]>
    >(`/api/admin/v2/billing_accounts/${accountId}/order_templates`, {
      params: { order_type: orderType },
    });
    return data.data;
  },

  async getOrderTemplate(templateId: number) {
    const { data } = await axiosRubyClient.get<
      ApiResponse<OrderTemplateDetail>
    >(`/api/admin/v2/order_templates/${templateId}`);
    return data.data;
  },

  async listSavedSnippets(accountId: number, fieldKind: SavedSnippetFieldKind) {
    const { data } = await axiosRubyClient.get<ApiResponse<SavedSnippet[]>>(
      `/api/admin/v2/billing_accounts/${accountId}/saved_snippets`,
      { params: { field_kind: fieldKind } },
    );
    return data.data;
  },

  async createSavedSnippet(
    accountId: number,
    payload: CreateSavedSnippetPayload,
  ) {
    const { data } = await axiosRubyClient.post<ApiResponse<SavedSnippet>>(
      `/api/admin/v2/billing_accounts/${accountId}/saved_snippets`,
      payload,
    );
    return data.data;
  },

  async applySavedSnippet(snippetId: number) {
    const { data } = await axiosRubyClient.post<ApiResponse<SavedSnippet>>(
      `/api/admin/v2/saved_snippets/${snippetId}/apply`,
    );
    return data.data;
  },

  async listShiftSupervisors(accountId: number) {
    const { data } = await axiosRubyClient.get<ApiResponse<ShiftSupervisor[]>>(
      `/api/admin/v2/billing_accounts/${accountId}/shift_supervisors`,
    );
    return data.data;
  },

  async createShiftSupervisor(
    accountId: number,
    payload: CreateShiftSupervisorPayload,
  ) {
    const { data } = await axiosRubyClient.post<ApiResponse<ShiftSupervisor>>(
      `/api/admin/v2/billing_accounts/${accountId}/shift_supervisors`,
      payload,
    );
    return data.data;
  },

  async searchWorkers(params: WorkerSearchParams) {
    const { data } = await axiosRubyClient.get<ApiResponse<WorkerListItem[]>>(
      '/api/admin/v2/workers/search',
      { params },
    );
    return data.data;
  },

  async getWorkerCounts(params: WorkerSearchParams) {
    const { data } = await axiosRubyClient.get<ApiResponse<WorkerCounts>>(
      '/api/admin/v2/workers/counts',
      { params },
    );
    return data.data;
  },

  async listLocations(accountId: number) {
    const { data } = await axiosRubyClient.get<ApiResponse<LocationListItem[]>>(
      `/api/admin/v2/billing_accounts/${accountId}/locations`,
    );
    return data.data;
  },

  async createLocation(accountId: number, payload: CreateLocationPayload) {
    const { data } = await axiosRubyClient.post<ApiResponse<LocationListItem>>(
      `/api/admin/v2/billing_accounts/${accountId}/locations`,
      payload,
    );
    return data.data;
  },

  async listSkills(accountId: number, search?: string) {
    const { data } = await axiosRubyClient.get<ApiResponse<SkillListItem[]>>(
      `/api/admin/v2/billing_accounts/${accountId}/skills`,
      { params: search ? { search } : undefined },
    );
    return data.data;
  },

  async listUniforms(accountId: number) {
    const { data } = await axiosRubyClient.get<ApiResponse<UniformListItem[]>>(
      `/api/admin/v2/billing_accounts/${accountId}/uniforms`,
    );
    return data.data;
  },

  async listPaymentMethods(accountId: number) {
    const { data } = await axiosRubyClient.get<
      ApiResponse<PaymentMethodsResponse>
    >(`/api/admin/v2/billing_accounts/${accountId}/payment_methods`);
    return data.data;
  },

  async getRateQuote(accountId: number, skillId: number, addressId: number) {
    const { data } = await axiosRubyClient.get<ApiResponse<RateQuoteV2>>(
      `/api/admin/v2/billing_accounts/${accountId}/rate_quote`,
      { params: { skill_id: skillId, address_id: addressId } },
    );
    return data.data;
  },

  async getRateRange(accountId: number, skillId: number, addressId: number) {
    const { data } = await axiosRubyClient.get<ApiResponse<RateRangeV2>>(
      `/api/admin/v2/billing_accounts/${accountId}/rate_range`,
      { params: { skill_id: skillId, address_id: addressId } },
    );
    return data.data;
  },

  async createOrder(payload: CreateOrderPayloadV2) {
    const { data } = await axiosRubyClient.post<CreateOrderResponseV2>(
      '/api/admin/v2/orders',
      payload,
    );
    return data;
  },
};
